import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/supabase-auth";
import { useUserRole } from "@/hooks/useUserRole";
import { PageLoader } from "./PageLoader";
import { buildReturnToUrl, homeRouteFor, parseReturnTo } from "@/utils/routing";

interface ProtectedRouteProps {
  children: ReactNode;
  requireRole?: string[];
  fallbackPath?: string;
}

export function ProtectedRoute({ 
  children, 
  requireRole,
  fallbackPath = "/login" 
}: ProtectedRouteProps) {
  const location = useLocation();
  const { session, loading } = useAuth();
  const { isLoading: rolesLoading, hasRole } = useUserRole();

  // CRITICAL FIX: Wait for access_token before rendering children
  // Prevents premature data queries that fail with 401
  if (loading || rolesLoading || (session && !session.access_token)) {
    return <PageLoader />;
  }

  // Redirect to login if not authenticated
  if (!session) {
    const destination = fallbackPath.startsWith("/login")
      ? buildReturnToUrl(fallbackPath, `${location.pathname}${location.search}`)
      : fallbackPath;

    return <Navigate to={destination} replace />;
  }

  // Check role requirements if specified
  if (requireRole && requireRole.length > 0) {
    const hasRequiredRole = requireRole.some(role =>
      hasRole(role as 'admin' | 'organization_admin' | 'parent' | 'user')
    );
    
    if (!hasRequiredRole) {
      return <Navigate to="/account-type-selection" replace />;
    }
  }

  return <>{children}</>;
}

interface PublicRouteProps {
  children: ReactNode;
  redirectPath?: string;
}

export function PublicRoute({ 
  children, 
  redirectPath 
}: PublicRouteProps) {
  const location = useLocation();
  const { session, loading, isOAuthCallback } = useAuth();
  const { isLoading: rolesLoading, roles } = useUserRole();

  // CRITICAL: Show loading IMMEDIATELY if OAuth callback is in progress
  // This prevents any flash of intermediate pages during OAuth flow
  if (isOAuthCallback) {
    return <PageLoader />;
  }

  // Show loading spinner while auth state is being determined
  if (loading || rolesLoading) {
    return <PageLoader />;
  }

  // Redirect authenticated users to role-appropriate dashboard (or return_to)
  if (session) {
    const fallbackPath = redirectPath || homeRouteFor(roles);
    const returnPath = parseReturnTo(new URLSearchParams(location.search), fallbackPath);

    // SECURITY FIX: Guard debug logs to prevent PII exposure in production
    if (import.meta.env.DEV) {
      console.debug('[PublicRoute] Redirecting authenticated user to:', returnPath);
    }

    return <Navigate to={returnPath} replace />;
  }

  return <>{children}</>;
}
